import { useCallback, useSyncExternalStore } from 'react';
import { NotificationSettingKey, NotificationSettingsState } from '../types';

const STORAGE_KEY = 'notificationSettings';

const DEFAULT_SETTINGS: NotificationSettingsState = {
	liquidationAlert: true,
	liquidationWarning: true,
	orderTriggered: true,
	orderFilled: true,
	positionClosed: true,
	delistNotice: true,
	newMarket: true,
	newsAndAnnouncements: true,
};

let listeners: Array<() => void> = [];

function emitChange() {
	for (const listener of listeners) {
		listener();
	}
}

function getSettings(): NotificationSettingsState {
	try {
		const stored = localStorage.getItem(STORAGE_KEY);
		if (stored) {
			return { ...DEFAULT_SETTINGS, ...JSON.parse(stored) };
		}
	} catch {
		// ignore
	}
	return DEFAULT_SETTINGS;
}

let cachedSnapshot: NotificationSettingsState = getSettings();

function setSettings(settings: NotificationSettingsState) {
	localStorage.setItem(STORAGE_KEY, JSON.stringify(settings));
	cachedSnapshot = settings;
	emitChange();
}

function subscribe(listener: () => void) {
	listeners = [...listeners, listener];
	return () => {
		listeners = listeners.filter((l) => l !== listener);
	};
}

function getSnapshot(): NotificationSettingsState {
	return cachedSnapshot;
}

function getServerSnapshot(): NotificationSettingsState {
	return DEFAULT_SETTINGS;
}

export function useNotificationSettings() {
	const settings = useSyncExternalStore(
		subscribe,
		getSnapshot,
		getServerSnapshot
	);

	const toggleSetting = useCallback((key: NotificationSettingKey) => {
		const current = getSettings();
		setSettings({ ...current, [key]: !current[key] });
	}, []);

	return { settings, toggleSetting };
}
